import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  ArrowRight, 
  Siren, 
  Heart, 
  Baby, 
  HeartPulse, 
  Bone, 
  ScanLine, 
  FlaskConical, 
  Flower2,
  Shield,
  Clock,
  Award,
  Users,
  CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Siren: Siren,
  Heart: Heart,
  Baby: Baby,
  HeartPulse: HeartPulse,
  Bone: Bone,
  ScanLine: ScanLine,
  FlaskConical: FlaskConical,
  Flower2: Flower2,
};

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, statsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/stats`),
        ]);
        setServices(servicesRes.data.slice(0, 6));
        setStats(statsRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section miami-grid relative pt-20">
        <div className="hero-gradient hero-gradient-1" />
        <div className="hero-gradient hero-gradient-2" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="stagger-children">
              <span className="inline-block px-4 py-2 bg-[#1a0b2e] border border-[#4b0082] rounded-full text-[#ff00ff] text-sm font-mono uppercase tracking-widest mb-6">
                HEALTHCENTRIX LLC - Multi-State Medical Center
              </span>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6">
                <span className="text-white">Healthcare</span>
                <br />
                <span className="text-gradient">Reimagined</span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed">
              Experience comprehensive medical care with our team of board-certified specialists. 
              From urgent care to routine checkups, we're here for you and your family 24/7.
            </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/appointment">
                  <Button 
                    data-testid="hero-book-appointment"
                    className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-8 py-6 text-lg hover:bg-[#00c0cc] hover:shadow-[0_0_20px_rgba(0,240,255,0.6)] transition-all group"
                  >
                    Book Appointment
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    data-testid="hero-view-services"
                    variant="outline"
                    className="border-2 border-[#ff00ff] text-[#ff00ff] font-bold uppercase tracking-wider rounded-full px-8 py-6 text-lg hover:bg-[#ff00ff] hover:text-white hover:shadow-[0_0_20px_rgba(255,0,255,0.6)] transition-all"
                  >
                    View Services
                  </Button>
                </Link>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-6">
              {[
                { value: "15+", label: "Specialties", color: "#00f0ff" },
                { value: "50+", label: "Expert Doctors", color: "#ff00ff" },
                { value: "24/7", label: "Emergency Care", color: "#ff9e00" },
                { value: "98%", label: "Satisfaction", color: "#00f0ff" },
              ].map((stat, index) => (
                <div 
                  key={index}
                  className="glass rounded-2xl p-6 text-center card-hover"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div 
                    className="stat-number"
                    style={{ color: stat.color }}
                  >
                    {stat.value}
                  </div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="bg-[#1a0b2e]/50 border-y border-[#4b0082]/30 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Clock, label: "Quick Service", desc: "Minimal wait times" },
              { icon: Award, label: "Board Certified", desc: "Expert physicians" },
              { icon: Users, label: "Family Care", desc: "All ages welcome" },
              { icon: Shield, label: "Top Rated", desc: "5-star reviews" },
            ].map((feature, index) => (
              <div 
                key={index}
                className="flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-[#0f0518] border border-[#4b0082] flex items-center justify-center">
                  <feature.icon className="w-6 h-6 text-[#00f0ff]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm">{feature.label}</h4>
                  <p className="text-gray-400 text-xs">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="home-services-section" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">What We Offer</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              Our Medical Services
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Comprehensive healthcare services delivered by specialized medical professionals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Heart;
              return (
                <div
                  key={service.id}
                  data-testid={`service-card-${service.id}`}
                  className="service-card"
                  style={{ "--card-color": service.color }}
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${service.color}20` }}
                  >
                    <IconComponent 
                      className="w-7 h-7"
                      style={{ color: service.color }}
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{service.name}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                data-testid="view-all-services"
                variant="outline"
                className="border-2 border-[#4b0082] text-white font-semibold rounded-full px-8 py-6 hover:border-[#00f0ff] hover:text-[#00f0ff] transition-all"
              >
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-[#0f0518] to-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=600"
                alt="Modern medical facility"
                className="rounded-3xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#1a0b2e] border border-[#4b0082] rounded-2xl p-6 glass">
                <div className="text-4xl font-bold text-[#00f0ff]">98%</div>
                <div className="text-gray-400 text-sm">Patient Satisfaction Rate</div>
              </div>
            </div>

            <div>
              <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Why Choose Us</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
                Why Choose HealthCentrix?
              </h2>
              <p className="text-gray-400 mb-8">
                We combine cutting-edge medical technology with compassionate care to deliver exceptional health outcomes.
              </p>

              <div className="space-y-4">
                {[
                  "Board-certified physicians across 15+ specialties",
                  "State-of-the-art diagnostic and treatment facilities",
                  "Same-day appointments and 24/7 emergency care",
                  "Accepts most major insurance plans",
                  "Electronic health records for seamless care coordination",
                  "Patient-centered approach with personalized treatment plans",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <CheckCircle2 className="w-6 h-6 text-[#00f0ff] shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/about" className="inline-block mt-8">
                <Button 
                  data-testid="learn-more-about"
                  className="bg-transparent border-2 border-[#00f0ff] text-[#00f0ff] font-bold uppercase tracking-wider rounded-full px-8 py-6 hover:bg-[#00f0ff] hover:text-black transition-all"
                >
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#00f0ff]/10 to-[#ff00ff]/10" />
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00f0ff] to-[#ff00ff]" />
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Experience Quality Healthcare?
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Schedule your appointment today and take the first step towards better health.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/appointment">
              <Button 
                data-testid="cta-book-appointment"
                className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg hover:bg-[#00c0cc] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
              >
                Book Appointment Now
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                data-testid="cta-contact-us"
                variant="outline"
                className="border-2 border-[#ff00ff] text-[#ff00ff] font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg hover:bg-[#ff00ff] hover:text-white transition-all"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
